import { forwardRef, useState, useEffect } from 'react';
import { usePermissions } from '@store/auth';

import { Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { TextField } from 'mui-rff';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import ClearIcon from '@mui/icons-material/Clear';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import FormStateToRedux from '@components/FormStateToRedux';

import {
  LOB_CHANNELS_FORM_NAME,
  PLATFORMS_MAIN,
} from '../../payment-options.constants';

function LOBChannelsForm({ initialValues, submitError = '' }, ref) {
  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  const [formValues, setFormValues] = useState({});
  const [hiddenPlatforms, setHiddenPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  const getFormPlatforms = (platforms) =>
    platforms.map((platform) => {
      const isMainPlatform = PLATFORMS_MAIN.includes(platform.platformCode);
      const hasChannelHash = !!platform.channelHash;

      return {
        ...platform,
        isMainPlatform,
        isAdded: isMainPlatform || hasChannelHash,
        hasChannelHash,
      };
    });

  useEffect(() => {
    setFormValues({
      ...initialValues,
      platforms: getFormPlatforms(initialValues.platforms),
    });
  }, [initialValues]);

  useEffect(() => {
    let platformOptions = [];

    if (formValues.platforms) {
      platformOptions = formValues.platforms
        .filter(
          ({ channelHash, isMainPlatform, isAdded = false }) =>
            !(channelHash || isMainPlatform || isAdded),
        )
        .sort((a, b) => a.platformName.localeCompare(b.platformName));
    }
    setHiddenPlatforms(platformOptions);
  }, [formValues]);

  const onSubmit = () => {
    // @TODO: implement
  };
  const handleRemoveChannel = (selected) => {
    const { platforms } = formValues;
    const valuesPlatformIdx = platforms.findIndex(
      (platform) => platform.platformCode === selected,
    );
    const platform = { ...formValues.platforms[valuesPlatformIdx] };

    platform.isAdded = false;

    const newValues = {
      ...formValues,
      platforms: [
        ...platforms.slice(0, valuesPlatformIdx),
        platform,
        ...platforms.slice(valuesPlatformIdx + 1),
      ],
    };
    setFormValues(newValues);
  };
  const handleSelectPlatform = ({ target: { value: selected } }) => {
    const { platforms } = formValues;
    const valuesPlatformIdx = platforms.findIndex(
      (platform) => platform.platformCode === selected,
    );
    const platform = { ...formValues.platforms[valuesPlatformIdx] };

    platform.isAdded = true;

    const newValues = {
      ...formValues,
      platforms: [...platforms, platform],
    };
    setFormValues(newValues);
    setSelectedPlatform('');
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <Form
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      initialValues={formValues}
      render={({ handleSubmit, form }) => (
        <form
          onSubmit={(e) =>
            process.env.REACT_APP_ENV !== 'production'
              ? handleSubmit(e)
              : handlePreSubmit(e)
          }
          onReset={() => {
            form.reset(); // triggered by external reset button
          }}
          ref={ref}>
          <ProdConfirmationDialog
            openDialog={openDialog}
            yesHandler={handleSubmit}
          />
          <FormStateToRedux form={LOB_CHANNELS_FORM_NAME} />
          {submitError && (
            <Alert
              sx={{ marginBottom: '10px' }}
              severity="error">
              {submitError}
            </Alert>
          )}
          <FieldArray name="platforms">
            {({ fields, meta }) =>
              fields.map((name, index) => {
                const {
                  isMainPlatform,
                  isAdded,
                  platformCode,
                  hasChannelHash,
                } = meta.initial[index];

                return isMainPlatform || isAdded ? (
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    key={`container.${name}`}
                    rowSpacing={0}>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      padding={2}
                      key={`platform.${name}`}>
                      <Typography variant="body1">
                        {meta.initial[index].platformName}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      container
                      justifyContent="center"
                      alignItems="center"
                      xs={12}
                      md={8}
                      spacing={0}>
                      <Grid
                        item
                        xs={11}>
                        <TextField
                          name={`${name}.channelHash`}
                          margin="dense"
                          key={`platforms.${name}`}
                          label="Channel Hash"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={1}>
                        <IconButton
                          disabled={hasChannelHash || !canEdit}
                          sx={{ color: 'secondary' }}
                          onClick={() => handleRemoveChannel(platformCode)}
                          type="button">
                          <ClearIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null;
              })
            }
          </FieldArray>
          {hiddenPlatforms && hiddenPlatforms.length && (
            <Grid
              container
              rowSpacing={0}
              justifyContent="center">
              <Grid
                item
                xs={4}>
                <FormControl
                  sx={{ paddingTop: '12px' }}
                  fullWidth
                  size="small">
                  <Select
                    disabled={!canEdit}
                    displayEmpty
                    value={selectedPlatform}
                    onChange={handleSelectPlatform}>
                    <MenuItem
                      disabled
                      value="">
                      Add Channel
                    </MenuItem>
                    {hiddenPlatforms.map((platform) => (
                      <MenuItem
                        key={platform.platformCode}
                        value={platform.platformCode}>
                        {platform.platformName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </form>
      )}
    />
  );
}

export default forwardRef(LOBChannelsForm);
